import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  Trash2,
  Shield,
  AlertCircle,
  ArrowLeft,
  RefreshCw,
  Plus
} from 'lucide-react';
import { api } from '../services/api';
import { useToast } from '../contexts/ToastContext';

interface Card {
  id: string;
  last_four_digits: string;
  expiration_month: number;
  expiration_year: number;
  cardholder: {
    name: string;
  };
  payment_method: {
    id: string;
    name: string;
    payment_type_id: string;
  };
  date_created: string;
  date_last_updated: string;
}

export const CardsManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<Card[]>([]);
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<Card | null>(null);

  const loadCards = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/payments/cards');

      const receivedCards = response.data?.data?.cards;
      if (response.data.success && Array.isArray(receivedCards)) {
        setCards(receivedCards);
      } else {
        setCards([]);
      }
    } catch (error: any) {
      console.error('Erro ao carregar cartões:', error);
      if (error.response?.status !== 404) {
        showError(error.response?.data?.message || 'Erro ao carregar cartões salvos');
      }
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const handleDeleteCard = async () => {
    if (!cardToDelete) return;

    try {
      setDeletingCardId(cardToDelete.id);
      const response = await api.delete(`/payments/cards/${cardToDelete.id}`);

      if (response.data.success) {
        showSuccess('Cartão removido com sucesso!');
        setCards(cards.filter(c => c.id !== cardToDelete.id));
        setShowDeleteModal(false);
        setCardToDelete(null);
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao remover cartão');
    } finally {
      setDeletingCardId(null);
    }
  };

  const openDeleteModal = (card: Card) => {
    setCardToDelete(card);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setCardToDelete(null);
  };

  const getCardBrand = (brandId: string): { name: string; color: string } => {
    const brands: Record<string, { name: string; color: string }> = {
      visa: { name: 'Visa', color: '#1434CB' },
      master: { name: 'Mastercard', color: '#EB001B' },
      amex: { name: 'American Express', color: '#006FCF' },
      elo: { name: 'Elo', color: '#FFCB05' },
      hipercard: { name: 'Hipercard', color: '#D4145A' }
    };

    return brands[brandId] || { name: brandId, color: '#666' };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const isCardExpired = (month: number, year: number) => {
    const now = new Date();
    const expiry = new Date(year, month - 1);
    return expiry < now;
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <RefreshCw size={40} />
          <p>Carregando cartões salvos...</p>
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/billing/subscription')}>
          <ArrowLeft size={20} />
          Voltar para Assinatura
        </BackButton>
        <TitleSection>
          <Title>Gerenciamento de Cartões</Title>
          <Subtitle>Gerencie seus métodos de pagamento salvos</Subtitle>
        </TitleSection>
      </Header>

      <SecurityNotice>
        <Shield size={20} />
        <div>
          <NoticeTitle>Segurança</NoticeTitle>
          <NoticeText>
            Seus dados de cartão são armazenados de forma segura pelo MercadoPago.
            Nunca armazenamos informações completas do cartão em nossos servidores.
          </NoticeText>
        </div>
      </SecurityNotice>

      {cards.length === 0 ? (
        <EmptyState>
          <CreditCard size={60} />
          <h3>Nenhum cartão salvo</h3>
          <p>Você ainda não possui cartões salvos. Adicione um cartão ao fazer um pagamento.</p>
          <Button onClick={() => navigate('/plans')}>
            <Plus size={18} />
            Ver Planos
          </Button>
        </EmptyState>
      ) : (
        <CardsGrid>
          {cards.map((card) => {
            const brand = getCardBrand(card.payment_method.id);
            const expired = isCardExpired(card.expiration_month, card.expiration_year);

            return (
              <CardItem key={card.id}>
                <CardHeader>
                  <CardBrand color={brand.color}>{brand.name}</CardBrand>
                  {expired && (
                    <ExpiredBadge>
                      <AlertCircle size={14} />
                      Expirado
                    </ExpiredBadge>
                  )}
                </CardHeader>

                <CardBody>
                  <CardNumber>•••• •••• •••• {card.last_four_digits}</CardNumber>
                  <CardHolder>{card.cardholder.name}</CardHolder>
                  <CardExpiry>
                    Validade: {String(card.expiration_month).padStart(2, '0')}/{card.expiration_year}
                  </CardExpiry>
                </CardBody>

                <CardFooter>
                  <CardInfo>
                    <InfoLabel>Adicionado em:</InfoLabel>
                    <InfoValue>{formatDate(card.date_created)}</InfoValue>
                  </CardInfo>
                  <DeleteButton
                    onClick={() => openDeleteModal(card)}
                    disabled={deletingCardId === card.id}
                  >
                    {deletingCardId === card.id ? (
                      <RefreshCw size={16} className="spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    Remover
                  </DeleteButton>
                </CardFooter>
              </CardItem>
            );
          })}
        </CardsGrid>
      )}

      <InfoSection>
        <InfoTitle>Por que gerenciar meus cartões?</InfoTitle>
        <InfoList>
          <InfoItem>
            <CheckIcon>✓</CheckIcon>
            <div>
              <strong>Pagamentos mais rápidos:</strong> Cartões salvos facilitam renovações automáticas de assinaturas.
            </div>
          </InfoItem>
          <InfoItem>
            <CheckIcon>✓</CheckIcon>
            <div>
              <strong>Controle total:</strong> Remova cartões que não utiliza mais a qualquer momento.
            </div>
          </InfoItem>
          <InfoItem>
            <CheckIcon>✓</CheckIcon>
            <div>
              <strong>Segurança:</strong> Todos os dados são criptografados e protegidos pelo MercadoPago.
            </div>
          </InfoItem>
        </InfoList>
      </InfoSection>

      {showDeleteModal && cardToDelete && (
        <Modal>
          <ModalOverlay onClick={closeDeleteModal} />
          <ModalContent>
            <ModalHeader>
              <AlertCircle size={24} color="#ff4757" />
              <h3>Remover Cartão</h3>
            </ModalHeader>
            <ModalBody>
              <p>Tem certeza que deseja remover este cartão?</p>
              <CardPreview>
                <CreditCard size={20} />
                <div>
                  <strong>{getCardBrand(cardToDelete.payment_method.id).name}</strong>
                  <div>•••• {cardToDelete.last_four_digits}</div>
                </div>
              </CardPreview>
              <p>
                Se este cartão estiver vinculado a assinaturas ativas, você precisará
                adicionar um novo método de pagamento.
              </p>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={closeDeleteModal} disabled={deletingCardId !== null}>
                Cancelar
              </CancelButton>
              <ConfirmButton onClick={handleDeleteCard} disabled={deletingCardId !== null}>
                {deletingCardId ? 'Removendo...' : 'Sim, Remover'}
              </ConfirmButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: none;
  margin: 0;
  min-height: 100vh;
  background: #f8f9fa;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #00d4aa;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: 2rem;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: #64748b;
  margin: 0;
`;

const SecurityNotice = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #ecfdf5;
  border: 1px solid #99f6e4;
  border-radius: 12px;
  margin-bottom: 2rem;
  color: #0f766e;
`;

const NoticeTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.3rem;
  color: #0f766e;
`;

const NoticeText = styled.div`
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const CardItem = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 212, 170, 0.2);
    border-color: rgba(0, 212, 170, 0.3);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CardBrand = styled.div<{ color: string }>`
  font-weight: 700;
  font-size: 1.1rem;
  color: ${props => props.color};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ExpiredBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  background: rgba(255, 71, 87, 0.2);
  border: 1px solid #ff4757;
  color: #ff4757;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const CardBody = styled.div`
  margin-bottom: 1.5rem;
`;

const CardNumber = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: 2px;
  margin-bottom: 0.8rem;
  font-family: 'Courier New', monospace;
`;

const CardHolder = styled.div`
  font-size: 0.95rem;
  color: #475569;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  font-weight: 500;
`;

const CardExpiry = styled.div`
  font-size: 0.85rem;
  color: #64748b;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const InfoLabel = styled.span`
  font-size: 0.75rem;
  color: #64748b;
`;

const InfoValue = styled.span`
  font-size: 0.85rem;
  color: #475569;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: rgba(255, 71, 87, 0.2);
  border: 1px solid #ff4757;
  color: #ff4757;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 71, 87, 0.3);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const InfoSection = styled.div`
  padding: 2rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const InfoTitle = styled.h3`
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  font-size: 1.2rem;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 1rem;
  color: #475569;
  line-height: 1.6;

  strong {
    color: #0f172a;
  }
`;

const CheckIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 212, 170, 0.2);
  color: #00d4aa;
  border-radius: 50%;
  flex-shrink: 0;
  font-weight: 700;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  color: #00d4aa;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  p {
    color: #64748b;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  text-align: center;
  color: #999;
  margin-bottom: 2rem;

  svg {
    color: #666;
  }

  h3 {
    color: #0f172a;
    margin: 0;
  }

  p {
    margin: 0;
    max-width: 400px;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #00d4aa;
  color: #0a0e17;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 212, 170, 0.3);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
`;

const ModalContent = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  z-index: 1001;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  h3 {
    color: #1f2937;
    margin: 0;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 2rem;

  p {
    color: #475569;
    margin-bottom: 1rem;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const CardPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin: 1rem 0;
  color: #1f2937;

  strong {
    display: block;
    margin-bottom: 0.3rem;
  }

  div {
    font-size: 0.9rem;
    color: #64748b;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #1f2937;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e2e8f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #ff4757;
  border: none;
  color: #fff;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #ff3444;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
